import { SignedIn, SignedOut, useUser } from "components/clerk";
import { Box, Button, Divider, HStack, Icon, Image, Link, Text, View, VStack } from "components/ui";
import { H2, H3, H4, Paragraph } from "components/ui/Typography";
import { usePathname } from "expo-router";
import { ChevronLeft, ChevronRight, User, X, Calendar, Settings } from "lucide-react-native";
import React, { useState } from "react";
import { ActivityIndicator, Platform } from "react-native";
import { useGetCategoryBySlugQuery, useGetCategoryQuery, useGetArticleQuery, useGetOrderQuery, useGetSitesQuery } from "store/abcoApi";
import { capitalizeFirstLetter } from "utils/globalFunctions";
import { useIsAdminSection, useSiteRegion } from "utils/hooks/useSiteSection";
import { subNavigationCollection, servicesNavigationItems, sustainabilityNavigationItems, aboutUsNavigationItems } from "./navigationData";
import { useSlideNavigation } from "./SlideNavigation";
import { SignInButton } from "@clerk/clerk-react";

type MobileMenuProps = {};

export default function MobileMenu() {
	const { data: categoryData } = useGetCategoryQuery();
	const { isSignedIn, user } = useUser();
	const shouldSkip = !isSignedIn || (user?.publicMetadata && Object.keys(user.publicMetadata).length === 0);
	let currentPath = usePathname();
	const isAdminSection = useIsAdminSection();
	const [currentPage, setCurrentPage] = useState<string>("");
	const [currentLevel, setCurrentLevel] = useState<number>(0);
	const [categorySlug, setCategorySlug] = useState<string>("");
	const { close } = useSlideNavigation("mobileMenu");
	let subNavigation = subNavigationCollection;
	const region = useSiteRegion();

	// API queries for dynamic content
	const { data: articleData } = useGetArticleQuery({
		pageSize: 5,
		pageNumber: 1,
	});
	const { data: orderData } = useGetOrderQuery(
		{
			pageNumber: 1,
			pageSize: 5,
			sortOrder: "desc",
		},
		{
			skip: shouldSkip,
		},
	);
	const { data: siteData } = useGetSitesQuery(
		{
			pageNumber: 1,
			sortField: "createdAt",
			// @ts-ignore
			sortDirection: "desc",
			pageSize: 5,
		},
		{
			skip: shouldSkip,
		},
	);

	if (!categoryData) {
		return null;
	}

	function goToMainMenu() {
		setCurrentLevel(0);
	}

	function goToSubMenu(page: string) {
		setCurrentPage(page);
		setCurrentLevel(1);
	}

	function goToCategoryMenu(slug: string) {
		setCategorySlug(slug);
		setCurrentLevel(2);
	}

	function goBackOneLevel() {
		setCurrentLevel(currentLevel - 1);
	}

	const MainMenu = () => (
		<Box paddingVertical={20} gap={12} paddingHorizontal={20} bg="$basicwhite" h="100%">
			<Button w={280} sx={{ justifyContent: "flex-start", paddingLeft: 0 }}>
				<Link href="/">
					<H3>Home</H3>
				</Link>
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("products")}>
				<H3>Products</H3>
				<ChevronRight color="black" />
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("equipment")}>
				<H3>Equipment</H3>
				<ChevronRight color="black" />
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("services")}>
				<H3>Services</H3>
				<ChevronRight color="black" />
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("sustainability")}>
				<H3>Sustainability</H3>
				<ChevronRight color="black" />
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("about-us")}>
				<H3>About Us</H3>
				<ChevronRight color="black" />
			</Button>
			<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("articles")}>
				<H3>Articles</H3>
				<ChevronRight color="black" />
			</Button>

			<SignedIn>
				<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("orders")}>
					<H3>Orders</H3>
					<ChevronRight color="black" />
				</Button>
				<Button w={"$full"} sx={{ justifyContent: "space-between", paddingLeft: 0 }} onPress={() => goToSubMenu("sites")}>
					<H3>Sites</H3>
					<ChevronRight color="black" />
				</Button>

				<Box py={32} bg="$basicwhite">
					<VStack space="lg" mx="$0.5">
						{subNavigation.map((props: any, key) => {
							const { title, href, icon } = props;
							let isSelected = props.href == currentPath;
							if (props.href == "/admin") {
								isSelected = isAdminSection;
							}
							return (
								<Link fontSize={14} key={key} href={href} fontWeight={isSelected ? "$bold" : "$normal"}>
									<HStack space="md">
										<Icon color="$primarylightblue" as={icon} size={"sm"} />
										<Text>{title}</Text>
									</HStack>
								</Link>
							);
						})}
					</VStack>
				</Box>
				<Link href="/profile">
					<Box justifyContent="center" flexDirection="row" gap={8} w={Platform.OS === "web" ? 235 : 250} py={8} borderWidth={1} borderColor="$primarylight" borderRadius={8}>
						<Icon as={User} color={"$primarylight"} />
						<Paragraph sx={{ color: "$primarylight" }}>Profile</Paragraph>
					</Box>
				</Link>
			</SignedIn>
			<Divider bg="$neutral100" my={16} w={240} />
			<SignedOut>
				<SignInButton fallbackRedirectUrl={"https://abcopro.co.nz"} forceRedirectUrl={"https://abcopro.co.nz"}>
					<H3>Login</H3>
				</SignInButton>
			</SignedOut>
		</Box>
	);

	const CategoriesMenu = () => {

		return (
			<View paddingHorizontal={20} h="100%">
				<Button paddingLeft={0} justifyContent="flex-start" onPress={goBackOneLevel}>
					<HStack gap={8} alignItems="center" paddingVertical={12}>
						<Icon as={ChevronLeft} color={"$primarylight"} />
						<Text color="$primarylight">Back</Text>
					</HStack>
				</Button>
				<Divider bg="$neutral100" my={16} w={240} />
				<H2>{capitalizeFirstLetter(currentPage)}</H2>
				<Box mt={12} style={Platform.OS === "web" ? ({ overflowY: "auto", height: "calc(100% - 100px)" } as any) : { flex: 1 }}>
					{/* Products - dynamic from categoryData */}
					{currentPage === "products" &&
						categoryData
							?.filter((category: any) => category.categoryType === "Products")
							?.map((category: any) => (
								<Box style={{ width: 250 }} my={12} key={category.slug}>
									<Button justifyContent="flex-start" paddingLeft={0} onPress={() => goToCategoryMenu(category.slug)}>
										<H4>{category.name}</H4>
									</Button>
								</Box>
							))}

					{/* Equipment - dynamic from categoryData + special items when logged in */}
					{currentPage === "equipment" && (
						<>
							{isSignedIn && (
								<>
									<Box style={{ width: 250 }} my={12}>
										<Link href="/equipment/my-equipment">
											<HStack gap={8} alignItems="center" py={8}>
												<Icon as={Settings} color="$primarylight" size="sm" />
												<H4>My Equipment</H4>
											</HStack>
										</Link>
									</Box>
									<Box style={{ width: 250 }} my={12}>
										<Link href="/equipment/service-orders">
											<HStack gap={8} alignItems="center" py={8}>
												<Icon as={Calendar} color="$primarylight" size="sm" />
												<H4>Service bookings</H4>
											</HStack>
										</Link>
									</Box>
									<Divider bg="$neutral100" my={16} w={240} />
								</>
							)}
							{categoryData
								?.filter((category: any) => category.categoryType === "Equipment")
								?.map((category: any) => (
									<Box style={{ width: 250 }} my={12} key={category.slug}>
										<Button justifyContent="flex-start" paddingLeft={0} onPress={() => goToCategoryMenu(category.slug)}>
											<H4>{category.name}</H4>
										</Button>
									</Box>
								))}
						</>
					)}

					{/* Services - static items to match MegaMenuContent */}
					{currentPage === "services" &&
						servicesNavigationItems.map((item, index) => (
							<Box style={{ width: 250 }} my={12} key={index}>
								<Link href={item.href}>
									<H4>{item.name}</H4>
								</Link>
							</Box>
						))}

					{/* Sustainability - static items */}
					{currentPage === "sustainability" &&
						sustainabilityNavigationItems.map((item, index) => (
							<Box style={{ width: 250 }} my={12} key={index}>
								<Link href={item.href}>
									<H4>{item.name}</H4>
								</Link>
							</Box>
						))}

					{/* About Us - static items */}
					{currentPage === "about-us" &&
						aboutUsNavigationItems.map((item, index) => (
							<Box style={{ width: 250 }} my={12} key={index}>
								<Link href={item.href}>
									<H4>{item.name}</H4>
								</Link>
							</Box>
						))}

					{/* Articles - recent articles */}
					{currentPage === "articles" && (
						<>
							{articleData?.list?.map((article: any) => (
								<Box style={{ width: 250 }} my={12} key={article.slug}>
									<Link href={`/articles/${article.slug}`}>
										<H4>{article.pageName}</H4>
									</Link>
								</Box>
							))}
							<Divider bg="$neutral100" my={16} w={240} />
							<Box style={{ width: 250 }} my={12}>
								<Link href="/articles">
									<H4>Browse all articles</H4>
								</Link>
							</Box>
						</>
					)}

					{/* Orders - recent orders */}
					{currentPage === "orders" && isSignedIn && (
						<>
							{orderData?.list?.map((order: any) => {
								let url = `/orders/${order.documentNumber}`;
								if (order.type == "cart") {
									url = `/cart/${order.id}`;
								}
								return (
									<Box style={{ width: 250 }} my={12} key={order.id}>
										<Link href={url}>
											<H4>
												{order.documentNumber} - {order.siteName ? order.siteName : "No Site"}
											</H4>
										</Link>
									</Box>
								);
							})}
							<Divider bg="$neutral100" my={16} w={240} />
							<Box style={{ width: 250 }} my={12}>
								<Link href="/orders">
									<H4>Browse all orders</H4>
								</Link>
							</Box>
						</>
					)}

					{/* Sites - recent sites */}
					{currentPage === "sites" && isSignedIn && (
						<>
							{siteData?.list?.map((site: any) => (
								<Box style={{ width: 250 }} my={12} key={site.id}>
									<Link href={`/sites/${site.id}`}>
										<H4>{site.name}</H4>
									</Link>
								</Box>
							))}
							<Divider bg="$neutral100" my={16} w={240} />
							<Box style={{ width: 250 }} my={12}>
								<Link href="/sites">
									<H4>Browse all sites</H4>
								</Link>
							</Box>
						</>
					)}
				</Box>
			</View>
		);
	};

	const SubCategoriesMenu = () => {
		const { data: category, isLoading } = useGetCategoryBySlugQuery({
			slug: String(categorySlug),
		});

		if (isLoading || !category) {
			return (
				<Box minHeight={300} width={250} marginTop={20}>
					<ActivityIndicator size={"large"} color={"#1A428A"} style={{ transform: [{ scaleX: 1.5 }, { scaleY: 1.5 }] }} />
				</Box>
			);
		}

		return (
			<View paddingHorizontal={20} h="100%">
				<Button paddingLeft={0} justifyContent="flex-start" onPress={goBackOneLevel}>
					<HStack gap={8} alignItems="center" paddingVertical={12}>
						<Icon as={ChevronLeft} color={"$primarylight"} />
						<Text color="$primarylight">Back</Text>
					</HStack>
				</Button>
				<Divider bg="$neutral100" my={16} w={240} />
				<H2 style={{ width: 250, lineHeight: 32 }}>{category.name}</H2>
				<Box mt={12} style={{ overflowY: "auto", height: "calc(100% - 100px)" }}>
					{category.subCategoryCollection?.map((subCategory: any) => {
						// Construct the URL based on the category type
						const baseUrl = `/${currentPage}/${category.slug}/${subCategory.slug}`;
						const url = subCategory.url || baseUrl;

						return (
							<Box style={{ width: 250 }} my={12} key={subCategory.slug}>
								<Link href={url}>
									<H4>{subCategory.name}</H4>
								</Link>
							</Box>
						);
					})}
				</Box>
			</View>
		);
	};

	return (
		<Box bg={"$neutral25"} style={{ height: "100vh", width: "100%" }}>
			<HStack paddingHorizontal={20} paddingVertical={16} w={"$full"} justifyContent="space-between">
				<Box>
					{region === "au" && <Image style={{ width: 141, height: 35 }} resizeMode="contain" source={require("assets/abco-logo.png")} alt="Abco Logo" />}
					{region === "nz" && <Image resizeMode="contain" source={require("assets/logo-nz-new.svg")} alt="Abco: The Power to Clean 7" style={{ width: 250, height: 72 }} />}
				</Box>
				<Box justifyContent="center">
					<Button onPress={close}>
						<Icon color="$basicblack" size={"md"} marginRight={"auto"} as={X} />
					</Button>
				</Box>
			</HStack>

			<Box style={Platform.OS === "web" ? ({ height: "calc(100% - 80px)", overflowX: "hidden", position: "relative" } as any) : { flex: 1, position: "relative" }}>
				<Box
					style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						transform: Platform.OS === "web" ? `translateX(${currentLevel === 0 ? 0 : "-100%"})` : undefined,
						...(Platform.OS === "web" && { transition: "transform 0.3s ease-in-out" }),
					}}
				>
					<MainMenu />
				</Box>

				<Box
					style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						transform: Platform.OS === "web" ? `translateX(${currentLevel === 1 ? 0 : currentLevel === 0 ? "100%" : "-100%"})` : undefined,
						...(Platform.OS === "web" && { transition: "transform 0.3s ease-in-out" }),
					}}
				>
					<CategoriesMenu />
				</Box>

				<Box
					style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						transform: Platform.OS === "web" ? `translateX(${currentLevel === 2 ? 0 : "100%"})` : undefined,
						...(Platform.OS === "web" && { transition: "transform 0.3s ease-in-out" }),
					}}
				>
					<SubCategoriesMenu />
				</Box>
			</Box>
		</Box>
	);
}
